/**
 * AIActionCard — Confirmation UI for destructive AI actions
 *
 * Shows a confirmation prompt when the AI wants to perform
 * a destructive action (delete task, remove member, etc.).
 *
 * States: pending → confirming → confirmed | cancelled
 */

import React, { useState } from 'react';
import { cn } from '../../utils/cn';

export default function AIActionCard({ action, onConfirm, onCancel, isLoading = false }) {
  const actionResult = action?.result || {};
  // Local state only tracks the transient "confirming" (in-flight) phase.
  // Permanent states (confirmed/cancelled/pending) are derived from props.
  const [isConfirming, setIsConfirming] = useState(false);

  const derivedStatus =
    (action?.confirmed || actionResult.confirmed) ? 'confirmed'
      : (action?.cancelled || actionResult.cancelled) ? 'cancelled'
      : isConfirming ? 'confirming'
      : 'pending';

  if (!action || !actionResult.requiresConfirmation) return null;

  const handleConfirm = async () => {
    setIsConfirming(true);
    await onConfirm?.(actionResult.pendingId);
    // Don't set confirmed here — the parent updates action.confirmed via setMessages
    setIsConfirming(false);
  };

  const handleCancel = async () => {
    await onCancel?.(actionResult.pendingId);
    // Parent updates action.cancelled via setMessages
  };

  return (
    <div
      role="status"
      aria-label={`Action ${derivedStatus}`}
      className={cn(
      'rounded-xl border p-3.5 transition-all duration-200',
      derivedStatus === 'confirmed'
        ? 'border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-950/20'
        : derivedStatus === 'cancelled'
        ? 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 opacity-60'
        : 'border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-950/20'
    )}>
      {/* Header icon + label */}
      <div className="flex items-center gap-2 mb-2">
        {derivedStatus === 'confirmed' ? (
          <div className="w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <svg className="w-3 h-3 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        ) : derivedStatus === 'cancelled' ? (
          <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        ) : (
          <div className="w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <svg className="w-3 h-3 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" />
            </svg>
          </div>
        )}
        <span className={cn(
          'text-sm font-medium',
          derivedStatus === 'confirmed' && 'text-emerald-700 dark:text-emerald-300',
          derivedStatus === 'cancelled' && 'text-gray-500 dark:text-gray-300',
          derivedStatus === 'pending' && 'text-amber-700 dark:text-amber-300',
          derivedStatus === 'confirming' && 'text-amber-600 dark:text-amber-400'
        )}>
          {derivedStatus === 'confirmed' && 'Action Executed'}
          {derivedStatus === 'cancelled' && 'Action Cancelled'}
          {derivedStatus === 'pending' && 'Confirmation Required'}
          {derivedStatus === 'confirming' && 'Executing…'}
        </span>
      </div>

      {/* Action details */}
      {actionResult.actionName && (
        <p className="text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">
          {actionResult.actionName}
        </p>
      )}
      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
        {actionResult.confirmMessage || 'This action cannot be undone. Are you sure?'}
      </p>

      {/* Buttons */}
      {derivedStatus === 'pending' && (
        <div className="flex gap-2">
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className="flex-1 px-3 py-1.5 text-xs font-medium rounded-lg
              bg-red-600 hover:bg-red-700 text-white
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors duration-150"
            aria-label="Confirm action"
          >
            Confirm
          </button>
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className="flex-1 px-3 py-1.5 text-xs font-medium rounded-lg
              bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700
              text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors duration-150"
            aria-label="Cancel action"
          >
            Cancel
          </button>
        </div>
      )}

      {derivedStatus === 'confirming' && (
        <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
          <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Processing…
        </div>
      )}

      {/* Success message */}
      {derivedStatus === 'confirmed' && action.result && (
        <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
          {action.result.message || 'Done.'}
        </p>
      )}
    </div>
  );
}
